// apps/web/src/App.js

import React, {
  useEffect,
} from 'react'

import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { ThemeSwitcherProvider } from 'react-css-theme-switcher'

import store from './store'
import Layouts from './layouts'

import FullscreenManager from './components/FullscreenManager'
import IdleScreenManager from './components/IdleScreenManager'

import {
  OfflineSyncService,
  NetworkService,
} from 'offline'
import OfflineStatusIndicator
  from './components/OfflineStatusIndicator'

import { THEME_CONFIG } from './configs/AppConfig'

import './lang'
import mockServer from './mock'

const themes = {
  dark:
    `${process.env.PUBLIC_URL}/css/dark-theme.css`,

  light:
    `${process.env.PUBLIC_URL}/css/light-theme.css`,
}

const environment =
  process.env.NODE_ENV

const enableMock =
  process.env.REACT_APP_ENABLE_MOCK ===
  'true'

if (
  environment !== 'production' &&
  enableMock
) {
  mockServer({
    environment,
  })
}

function App() {
  /*
   * ==========================================================
   * SINCRONIZAÇÃO OFFLINE / ONLINE
   * ==========================================================
   *
   * IMPORTANTE:
   *
   * Não usamos navigator.onLine como fonte de verdade.
   *
   * navigator.onLine apenas informa se o navegador
   * acredita possuir uma conexão de rede.
   *
   * Isso não significa que nossa API esteja acessível.
   *
   * A confirmação real é feita através de:
   *
   * NetworkService.checkApiConnection()
   *
   * que consulta o /health da API.
   * ==========================================================
   */

  useEffect(() => {
    let cancelled = false

    /*
     * Evita duas sincronizações acontecendo
     * simultaneamente.
     *
     * Isso pode acontecer, por exemplo, se:
     *
     * - a aplicação iniciar;
     * - o evento "online" disparar logo depois.
     */
    let syncRunning = false

    const trySync =
      async () => {
        /*
         * Se o componente já foi desmontado,
         * não fazemos mais nada.
         */
        if (cancelled) {
          return
        }

        /*
         * Evita sincronizações concorrentes.
         */
        if (syncRunning) {
          console.log(
            '[SYNC] Sincronização já está em andamento.',
          )

          return
        }

        console.log(
          '[SYNC] Verificando disponibilidade real da API...',
        )

        try {
          /*
           * ==================================================
           * 1. TESTA A API REAL
           * ==================================================
           */

          const apiOnline =
            await NetworkService
              .checkApiConnection()

          if (cancelled) {
            return
          }

          /*
           * API realmente indisponível.
           *
           * Não tentamos sincronizar.
           */
          if (!apiOnline) {
            console.log(
              '[SYNC] API indisponível. Sincronização adiada.',
            )

            return
          }

          console.log(
            '[SYNC] API disponível. Verificando pendências...',
          )

          /*
           * ==================================================
           * 2. INICIA SINCRONIZAÇÃO
           * ==================================================
           */

          syncRunning = true

          window.dispatchEvent(
            new CustomEvent(
              'games:offline-sync-started',
            ),
          )

          const result =
            await OfflineSyncService
              .syncAll()

          if (cancelled) {
            return
          }

          console.log(
            '[SYNC] Sincronização concluída:',
            result,
          )

          /*
           * Avisamos outras partes da aplicação
           * que uma sincronização acabou.
           *
           * No futuro isso poderá atualizar:
           *
           * - indicadores;
           * - status offline;
           * - quantidade de pendências;
           * - painel administrativo;
           * - etc.
           */
          window.dispatchEvent(
            new CustomEvent(
              'games:offline-sync-finished',
              {
                detail:
                  result,
              },
            ),
          )
        } catch (error) {
          if (cancelled) {
            return
          }

          console.error(
            '[SYNC] Erro durante sincronização:',
            error,
          )
        } finally {
          syncRunning = false
        }
      }

    /*
     * ========================================================
     * EVENTO ONLINE
     * ========================================================
     *
     * O evento do navegador é apenas um gatilho.
     *
     * Quando ele disser que a internet voltou,
     * ainda confirmamos através do /health.
     * ========================================================
     */

    const handleOnline = () => {
      console.log(
        '[SYNC] Navegador informou retorno da conexão.',
      )

      trySync()
    }

    /*
     * ========================================================
     * EVENTO OFFLINE
     * ========================================================
     *
     * Apenas informativo.
     *
     * Não fazemos nenhuma requisição aqui.
     * ========================================================
     */

    const handleOffline = () => {
      console.log(
        '[SYNC] Navegador informou perda da conexão.',
      )
    }

    window.addEventListener(
      'online',
      handleOnline,
    )

    window.addEventListener(
      'offline',
      handleOffline,
    )

    /*
     * ========================================================
     * INICIALIZAÇÃO
     * ========================================================
     *
     * Quando a aplicação abre:
     *
     * NÃO verificamos navigator.onLine.
     *
     * Testamos diretamente nossa API.
     * ========================================================
     */

    trySync()

    /*
     * ========================================================
     * CLEANUP
     * ========================================================
     */

    return () => {
      cancelled = true

      window.removeEventListener(
        'online',
        handleOnline,
      )

      window.removeEventListener(
        'offline',
        handleOffline,
      )
    }
  }, [])

  /*
   * ==========================================================
   * APLICAÇÃO
   * ==========================================================
   */

  return (
    <div className="App">
      <Provider store={store}>
        <BrowserRouter>
          <FullscreenManager />

          <IdleScreenManager />

          <OfflineStatusIndicator />

          <ThemeSwitcherProvider
            themeMap={
              themes
            }

            defaultTheme={
              THEME_CONFIG
                .currentTheme
            }

            insertionPoint="styles-insertion-point"
          >
            <Layouts />
          </ThemeSwitcherProvider>
        </BrowserRouter>
      </Provider>
    </div>
  )
}

export default App